/**
 * Solo Grind Rules
 *
 * Pure rules layer for the single-player, house-banked dice modes. Mirrors
 * CrapsRules.ts / CeeloRules.ts so the solo strategy plugs into the same
 * GameStrategy engine the multiplayer games use.
 *
 * Two modes share this module:
 *   - Hi-Lo (Over/Under 7): 2 dice. Bet LO (2-6), HI (8-12), or SEVEN (7).
 *     A total of 7 on HI/LO is a RE-ROLL — the bet stays live, the player
 *     picks the dice back up and throws again (near-zero-edge grind). The
 *     house edge lives on the SEVEN lottery bet (pays 4:1).
 *   - Chuck-a-Luck: 3 dice. Pick one face 1-6, paid per matching die
 *     (1:1 / 2:1 / 3:1). One number per round.
 *
 * The "house" is the counterparty: settlement is a fixed payout table, NOT
 * the pari-mutuel fade model craps uses. The house "fade" is cosmetic.
 *
 * Zero side effects. No room/network references. Shared between client
 * (bet menu, payout preview) and server (authoritative settlement).
 */
/** Number of dice thrown for a given solo mode. */
export function soloDiceCount(mode) {
    return mode === 'hilo' ? 2 : 3;
}
/**
 * Server-authoritative bet codes offered for a mode (the `availableSidebets`
 * the client renders into selection icons).
 */
export function soloAvailableBets(mode) {
    if (mode === 'hilo')
        return ['lo', 'seven', 'hi'];
    return ['face_1', 'face_2', 'face_3', 'face_4', 'face_5', 'face_6'];
}
/**
 * Multiple of the stake the house puts up as its cosmetic "fade" cover for a
 * given bet (the max it could pay). Derivable without the dice, so the FADE
 * beat can show the house stack the moment the bet is placed.
 */
export function soloHouseStakeMultiple(mode, betType) {
    if (mode === 'hilo')
        return betType === 'seven' ? 4 : 1;
    return 3;
}
// ============================================================================
// TABLE LIMIT
// ============================================================================
/**
 * Max NET amount the house pays out on a single solo round (winnings, not
 * counting the returned stake). The per-bet stake cap derives from this via
 * the bet's payout multiple, so a capped SEVEN bet (4:1) can't extract more
 * per round than a capped LO/HI bet (1:1).
 *
 * Why a table limit at all: LO/HI is a fair 50/50, so an uncapped martingale
 * (double after every loss) wins a small amount almost every session against
 * the house, and the cap is what breaks the doubling ladder — same reason
 * real casinos cap even-money bets. It also bounds the per-round blast
 * radius of any abuse of the house-banked mode (dice results are
 * client-trusted, accepted balance-audit decision A1).
 */
export const SOLO_MAX_NET_WIN = 1_000_000_000;
/** Stake caps snap down to a clean player-facing step. */
const SOLO_MAX_STAKE_STEP = 50_000_000;
/**
 * Table limit for a bet's stake: the largest clean-step stake whose max net
 * payout stays within SOLO_MAX_NET_WIN. LO/HI (1:1) → 1B, SEVEN (4:1) →
 * 250M, chuck-a-luck (up to 3:1) → 300M. Shared by the server gate
 * (canCreateBetZone) and the client's limit messaging.
 */
export function soloMaxStake(mode, betType) {
    const multiple = soloHouseStakeMultiple(mode, betType);
    return Math.floor(SOLO_MAX_NET_WIN / multiple / SOLO_MAX_STAKE_STEP) * SOLO_MAX_STAKE_STEP;
}
/** Rows kept on the context / broadcast — oldest are dropped past this. */
export const SOLO_ROLL_HISTORY_MAX = 50;
// ============================================================================
// HI-LO (Over/Under 7)
// ============================================================================
export function evaluateHiLo(dice, bet, stake) {
    const total = dice[0] + dice[1];
    if (bet === 'seven') {
        // Lottery bet: 4:1. Win credits stake + 4× winnings.
        if (total === 7)
            return { outcome: 'win', payout: stake * 5, houseStakeMultiple: 4 };
        return { outcome: 'lose', payout: 0, houseStakeMultiple: 4 };
    }
    // HI / LO: a 7 re-rolls — no settlement, the bet stays live and the player
    // throws again (payout unused on this branch).
    if (total === 7)
        return { outcome: 'reroll', payout: 0, houseStakeMultiple: 1 };
    const isLo = total >= 2 && total <= 6; // total !== 7 here, so !isLo === HI (8-12)
    const won = bet === 'lo' ? isLo : !isLo;
    if (won)
        return { outcome: 'win', payout: stake * 2, houseStakeMultiple: 1 };
    return { outcome: 'lose', payout: 0, houseStakeMultiple: 1 };
}
// ============================================================================
// CHUCK-A-LUCK (one face per round)
// ============================================================================
/** Parse a chuck-a-luck bet code ('face_3') to its face number, or null. */
export function parseChuckFace(betType) {
    const m = /^face_([1-6])$/.exec(betType);
    return m ? Number(m[1]) : null;
}
export function evaluateChuckALuck(dice, face, stake) {
    const matches = dice.filter(d => d === face).length;
    if (matches === 0)
        return { outcome: 'lose', payout: 0, houseStakeMultiple: 3 };
    // 1 match → 1:1, 2 → 2:1, 3 → 3:1. Payout = stake + matches × stake.
    return { outcome: 'win', payout: stake * (1 + matches), houseStakeMultiple: 3 };
}
// ============================================================================
// UNIFIED ENTRY (used by the strategy's settlement)
// ============================================================================
/**
 * Evaluate a solo bet given the mode, the chosen bet code, the landed dice,
 * and the stake. Returns null if the bet code is invalid for the mode.
 */
export function evaluateSolo(mode, betType, dice, stake) {
    if (mode === 'hilo') {
        if (betType !== 'lo' && betType !== 'seven' && betType !== 'hi')
            return null;
        return evaluateHiLo([dice[0], dice[1]], betType, stake);
    }
    const face = parseChuckFace(betType);
    if (face === null)
        return null;
    return evaluateChuckALuck([dice[0], dice[1], dice[2]], face, stake);
}
//# sourceMappingURL=SoloRules.js.map